import React, { useState, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  X,
  CheckCheck,
  Trash2,
  Phone,
  Eye,
  EyeOff,
  Send,
  ExternalLink,
  BellRing,
  Clock,
  Bell,
  RefreshCcw,
} from "lucide-react";
import { toast } from "react-toastify";
import { useNotificationCenter } from "../../contexts/NotificationContext";
import { Customer, BirthdayNotification } from "../../types/loyalty.types";
import { SystemNotification } from "../../types/notifications.types";
import QuickWhatsAppSenderModal from "../loyalty/QuickWhatsAppSenderModal";
import { NotificationIcon } from "../notifications/NotificationIcon";
import { getCategoryColor } from "../notifications/NotificationColor";

// Utility for time-ago format
const formatTimeAgo = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return "Just now";
  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;
  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays === 1) return `Yesterday`;
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

interface NotificationPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

type FilterTabEnum =
  | "ALL"
  | "UNREAD"
  | "ORDER"
  | "INVENTORY"
  | "BUSINESS"
  | "LOYALTY"
  | "REVENUE"
  | "SUPPLIER"
  | "MENU"
  | "TABLE"
  | "CUSTOMER"
  | "MARKETING"
  | "OPERATIONAL";

const NotificationPanel: React.FC<NotificationPanelProps> = ({
  isOpen,
  onClose,
}) => {
  const {
    notifications,
    birthDayNotifications,
    unreadCount,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    clearAllNotifications,
    loadNotifications,
  } = useNotificationCenter();

  const navigate = useNavigate();
  const [filterType, setFilterType] = useState<FilterTabEnum>("ALL");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [selectedCustomerForMessage, setSelectedCustomerForMessage] =
    useState<Customer | null>(null);

  const handleScroll = (e: React.WheelEvent<HTMLDivElement>) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft += e.deltaY;
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      loadNotifications();
    }
  }, [isOpen, loadNotifications]);

  const allNotifications = useMemo(() => {
    const combined: (SystemNotification | BirthdayNotification)[] = [
      ...notifications,
      ...birthDayNotifications,
    ];

    const now = new Date().getTime();
    const recentOnly = combined.filter((n) => {
      const created = new Date(n.createdAt).getTime();
      return now - created <= 24 * 60 * 60 * 1000;
    });

    return recentOnly.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [notifications, birthDayNotifications]);

  const filteredNotifications = useMemo(() => {
    let filtered = [...allNotifications];

    if (filterType === "UNREAD") {
      filtered = filtered.filter((n) => !n.isRead);
    } else if (filterType !== "ALL") {
      filtered = filtered.filter((n) => {
        if ("customerId" in n) return filterType === "LOYALTY";
        return n.category === filterType;
      });
    }

    return filtered;
  }, [allNotifications, filterType]);

  const handleMarkAsRead = (id: string, isRead: boolean) => {
    if (isRead) {
      markAsUnread(id);
      toast.info("Marked as unread", {
        position: "bottom-left",
        autoClose: 2000,
      });
    } else {
      markAsRead(id);
      toast.success("Marked as read", {
        position: "bottom-left",
        autoClose: 2000,
      });
    }
  };

  const handleDelete = (id: string) => {
    deleteNotification(id);
    toast.info("Notification removed", {
      position: "bottom-left",
      autoClose: 2000,
    });
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    toast.success("All caught up!", {
      position: "bottom-left",
      autoClose: 2500,
    });
  };

  const handleClearAll = () => {
    clearAllNotifications();
    toast.info("Inbox cleared", { position: "bottom-left", autoClose: 2500 });
  };

  const handleSendMessage = (notification: BirthdayNotification) => {
    const customer: Customer = {
      id: notification.customerId,
      name: notification.customerName,
      phone: notification.customerPhone,
      email: notification.customerEmail || "",
      avatar: undefined,
      dateJoined: new Date().toISOString(),
      lastVisit: new Date().toISOString(),
      totalVisits: 0,
      totalSpent: 0,
      loyaltyTier: {
        id: "default",
        name: "Default",
        minPoints: 0,
        maxPoints: 0,
        benefits: [],
        multiplier: 1,
        color: "#000000",
        icon: "🎁",
      },
      points: 0,
      preferences: {
        favoriteItems: [],
        dietaryRestrictions: [],
        allergies: [],
        spiceLevel: "medium",
        preferredTimeSlots: [],
        communicationPreference: "none",
      },
      demographics: {
        birthdate: notification.birthdayDate,
        age: 0,
        gender: "",
        location: "",
        occupation: "",
      },
    };

    setSelectedCustomerForMessage(customer);
    setShowWhatsAppModal(true);
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
  };

  const handleNavigate = (url?: string, category?: string) => {
    let targetUrl = "/main";

    if (url && url !== "#" && url !== "/" && !url.includes("undefined")) {
      targetUrl = url;
    } else if (category) {
      switch (category.toUpperCase()) {
        case "ORDER":
          targetUrl = "/order-list";
          break;
        case "INVENTORY":
        case "SUPPLIER":
          targetUrl = "/inventory";
          break;
        case "MENU":
          targetUrl = "/admin-menu-list";
          break;
        case "TABLE":
          targetUrl = "/tables";
          break;
        case "CUSTOMER":
        case "LOYALTY":
          targetUrl = "/customer-list";
          break;
        case "REVENUE":
        case "BUSINESS":
        case "OPERATIONAL":
          targetUrl = "/main";
          break;
      }
    }

    navigate(targetUrl);
    onClose();
  };

  const getCategoryCount = (category: FilterTabEnum) => {
    return allNotifications.filter((n) => {
      if (category === "ALL") return true;
      if (category === "UNREAD") return !n.isRead;
      if ("customerId" in n) return category === "LOYALTY";
      return n.category === category;
    }).length;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/30 backdrop-blur-[2px] z-40 transition-opacity"
          />

          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-[500px] bg-slate-50 shadow-2xl z-50 flex flex-col sm:rounded-l-2xl border-l border-slate-200 overflow-hidden"
          >
            {/* ── HEADER ── */}
            <div className="px-5 py-4 border-b border-slate-200 bg-white relative z-10 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="relative flex items-center justify-center w-9 h-9 bg-slate-100 text-slate-700 rounded-lg border border-slate-200">
                  <BellRing className="w-4 h-4" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-orange-500 border border-white"></span>
                    </span>
                  )}
                </div>
                <div>
                  <h2 className="text-[15px] font-semibold text-slate-900 leading-tight">
                    Notifications
                  </h2>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    You have{" "}
                    <span className="font-medium text-slate-900">
                      {unreadCount}
                    </span>{" "}
                    unread
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={async () => {
                    setIsRefreshing(true);
                    await loadNotifications();
                    setTimeout(() => setIsRefreshing(false), 500);
                  }}
                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                  title="Refresh Notifications"
                >
                  <RefreshCcw
                    className={`w-4 h-4 ${isRefreshing ? "animate-spin text-slate-700" : ""}`}
                  />
                </button>
                <button
                  onClick={onClose}
                  className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                  title="Close Panel"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* ── FILTER TABS ── */}
            <div
              ref={scrollContainerRef}
              onWheel={handleScroll}
              className="px-4 py-2.5 bg-white border-b border-slate-200 flex gap-2 overflow-x-auto hide-scrollbar z-0 relative shadow-sm"
            >
              {[
                { label: "All", val: "ALL" },
                { label: "Unread", val: "UNREAD" },
                { label: "Orders", val: "ORDER" },
                { label: "Inventory", val: "INVENTORY" },
                { label: "Business", val: "BUSINESS" },
                { label: "Loyalty", val: "LOYALTY" },
                { label: "Menu", val: "MENU" },
                { label: "Table", val: "TABLE" },
                { label: "Marketing", val: "MARKETING" },
              ].map((tab) => (
                <FilterTab
                  key={tab.val}
                  label={tab.label}
                  count={getCategoryCount(tab.val as FilterTabEnum)}
                  isActive={filterType === tab.val}
                  onClick={() => setFilterType(tab.val as FilterTabEnum)}
                />
              ))}
            </div>

            {/* ── NOTIFICATIONS LIST ── */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
              {filteredNotifications.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center justify-center h-full text-center p-6"
                >
                  <div className="w-12 h-12 bg-white shadow-sm rounded-full flex items-center justify-center mb-3 border border-slate-200">
                    <Bell className="w-5 h-5 text-slate-300" strokeWidth={2} />
                  </div>
                  <h3 className="text-[13px] font-semibold text-slate-900">
                    All caught up!
                  </h3>
                  <p className="text-[12px] text-slate-500 mt-1 max-w-[220px]">
                    New alerts and updates will appear here.
                  </p>
                </motion.div>
              ) : (
                <AnimatePresence mode="popLayout">
                  {filteredNotifications.map((notification) => {
                    const isLegacy = "customerId" in notification;
                    if (isLegacy) {
                      return (
                        <LegacyNotificationItem
                          key={notification.id}
                          notification={notification as BirthdayNotification}
                          onToggleRead={() =>
                            handleMarkAsRead(
                              notification.id,
                              notification.isRead,
                            )
                          }
                          onDelete={() => handleDelete(notification.id)}
                          onSendMessage={() =>
                            handleSendMessage(
                              notification as BirthdayNotification,
                            )
                          }
                        />
                      );
                    } else {
                      return (
                        <SystemNotificationItem
                          key={notification.id}
                          notification={notification as SystemNotification}
                          onToggleRead={() =>
                            handleMarkAsRead(
                              notification.id,
                              notification.isRead,
                            )
                          }
                          onDelete={() => handleDelete(notification.id)}
                          onNavigate={() =>
                            handleNavigate(
                              notification.actionUrl,
                              notification.category,
                            )
                          }
                        />
                      );
                    }
                  })}
                </AnimatePresence>
              )}
            </div>

            {/* ── FOOTER ACTIONS ── */}
            {allNotifications.length > 0 && (
              <div className="p-3 border-t border-slate-200 bg-white grid grid-cols-2 gap-2 z-10">
                <button
                  onClick={handleMarkAllAsRead}
                  className="w-full px-3 py-2 bg-slate-100 text-slate-700 text-[12px] font-medium rounded-lg hover:bg-slate-200 transition-colors flex items-center justify-center gap-1.5"
                >
                  <CheckCheck className="w-3.5 h-3.5" />
                  Mark all read
                </button>
                <button
                  onClick={handleClearAll}
                  className="w-full px-3 py-2 bg-white text-rose-600 border border-slate-200 text-[12px] font-medium rounded-lg hover:bg-rose-50 hover:border-rose-200 transition-colors flex items-center justify-center gap-1.5"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Clear all
                </button>
              </div>
            )}
          </motion.div>

          {/* Modals */}
          {showWhatsAppModal && (
            <QuickWhatsAppSenderModal
              isOpen={showWhatsAppModal}
              customer={selectedCustomerForMessage}
              onClose={() => {
                setShowWhatsAppModal(false);
                setSelectedCustomerForMessage(null);
              }}
              onSuccess={() => {
                setShowWhatsAppModal(false);
                setSelectedCustomerForMessage(null);
              }}
            />
          )}
        </>
      )}
    </AnimatePresence>
  );
};

// ========================================
// SUB-COMPONENTS
// ========================================

const FilterTab: React.FC<{
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}> = ({ label, count, isActive, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 rounded-full text-[11px] font-medium whitespace-nowrap transition-colors flex items-center gap-1.5 flex-shrink-0 border 
      ${
        isActive
          ? "bg-slate-800 text-white border-slate-800"
          : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
      }`}
  >
    {label}
    {count > 0 && (
      <span
        className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold leading-none ${
          isActive ? "bg-slate-600 text-white" : "bg-slate-100 text-slate-500"
        }`}
      >
        {count}
      </span>
    )}
  </button>
);

const priorityStyles: Record<string, string> = {
  CRITICAL: "bg-rose-50 text-rose-600 border-rose-200",
  HIGH: "bg-orange-50 text-orange-600 border-orange-200",
  MEDIUM: "bg-amber-50 text-amber-600 border-amber-200",
  LOW: "bg-slate-100 text-slate-600 border-slate-200",
};

const SystemNotificationItem: React.FC<{
  notification: SystemNotification;
  onToggleRead: () => void;
  onDelete: () => void;
  onNavigate: () => void;
}> = ({ notification, onToggleRead, onDelete, onNavigate }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`group relative p-3 rounded-xl transition-all duration-200 border ${
        notification.isRead
          ? "bg-slate-50/70 border-slate-100 opacity-80 hover:opacity-100"
          : "bg-white border-slate-200 border-l-4 border-l-orange-500 shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)]"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Compact Icon */}
        <div
          className={`w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center border ${
            priorityStyles[notification.priority] ||
            "bg-slate-50 text-slate-600 border-slate-200"
          }`}
        >
          <NotificationIcon
            type={notification.notificationType}
            icon={notification.icon}
          />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start gap-2">
            <h3
              className={`text-[13px] leading-snug font-semibold truncate ${notification.isRead ? "text-slate-600" : "text-slate-900"}`}
            >
              {notification.title}
            </h3>
            <span className="text-[9px] font-medium text-slate-400 flex items-center gap-1 flex-shrink-0 mt-0.5">
              <Clock className="w-2.5 h-2.5 text-slate-300" />
              {formatTimeAgo(notification.createdAt)}
            </span>
          </div>

          <p className="text-[12px] mt-0.5 line-clamp-2 text-slate-500 leading-snug">
            {notification.message}
          </p>

          <div className="mt-2.5 flex items-center justify-between">
            <span
              className={`text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200`}
            >
              {notification.category}
            </span>

            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={onToggleRead}
                className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                title={notification.isRead ? "Mark as unread" : "Mark as read"}
              >
                {notification.isRead ? (
                  <EyeOff className="w-3.5 h-3.5" />
                ) : (
                  <Eye className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={onDelete}
                className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {notification.actionUrl && (
            <button
              onClick={onNavigate}
              className="mt-2.5 w-full py-1.5 px-3 bg-white hover:bg-slate-50 text-slate-700 text-[11px] font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-colors border border-slate-200 shadow-sm"
            >
              {notification.actionLabel || "View Details"}
              <ExternalLink className="w-3 h-3 text-slate-400" />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const LegacyNotificationItem: React.FC<{
  notification: BirthdayNotification;
  onToggleRead: () => void;
  onDelete: () => void;
  onSendMessage: () => void;
}> = ({ notification, onToggleRead, onDelete, onSendMessage }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`group relative p-3 rounded-xl transition-all duration-200 border ${
        notification.isRead
          ? "bg-slate-50/70 border-slate-100 opacity-80 hover:opacity-100"
          : "bg-white border-slate-200 border-l-4 border-l-emerald-500 shadow-[0_2px_8px_-3px_rgba(0,0,0,0.05)]"
      }`}
    >
      <div className="flex items-start gap-3">
        {/* Compact Icon */}
        <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center border bg-emerald-50 text-emerald-600 border-emerald-100">
          <NotificationIcon type={notification.notificationType} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-start gap-2">
            <h3
              className={`text-[13px] leading-snug font-semibold truncate ${notification.isRead ? "text-slate-600" : "text-slate-900"}`}
            >
              {notification.customerName}'s Birthday
            </h3>
            <span className="text-[9px] font-medium text-slate-400 flex items-center gap-1 flex-shrink-0 mt-0.5">
              <Clock className="w-2.5 h-2.5 text-slate-300" />
              {formatTimeAgo(notification.createdAt)}
            </span>
          </div>

          <p className="text-[12px] mt-0.5 text-slate-500 line-clamp-2 leading-snug">
            {notification.message}
          </p>

          {notification.customerPhone && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 bg-slate-50 px-1.5 py-0.5 rounded text-[11px] font-medium text-slate-600 border border-slate-200">
              <Phone className="w-3 h-3 text-slate-400" />
              {notification.customerPhone}
            </div>
          )}

          <div className="mt-2.5 flex items-center justify-between">
            <span className="text-[9px] uppercase font-bold tracking-widest px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-100">
              LOYALTY
            </span>

            <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={onToggleRead}
                className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition-colors"
                title={notification.isRead ? "Mark as unread" : "Mark as read"}
              >
                {notification.isRead ? (
                  <EyeOff className="w-3.5 h-3.5" />
                ) : (
                  <Eye className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={onDelete}
                className="p-1 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                title="Delete"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          <button
            onClick={onSendMessage}
            className="mt-2.5 w-full py-1.5 px-3 bg-[#25D366] hover:bg-[#20bd5a] text-white text-[11px] font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-colors shadow-sm"
          >
            <Send className="w-3.5 h-3.5" /> Send WhatsApp Message
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default NotificationPanel;
